/** @file Handballplan entry point. */
import "./styles.css";
import { mount, destroy as destroyStage } from "./render/stage.js";
import { initFirebase } from "./persistence/firebase.js";
import { mountToolbar, unmountToolbar } from "./ui/toolbar.js";
import { mountTokenEditor, openForToken, unmountTokenEditor } from "./ui/token-editor.js";
import { mountTimeline } from "./ui/timeline-view.js";
import { mountPlaybackControls } from "./ui/playback-controls.js";
import { createPlaybackEngine } from "./playback/engine.js";
import { getLayers } from "./render/stage.js";
import { mountArrowScenePopover } from "./ui/arrow-scene-popover.js";
import { mountHeader, setHeaderVisible } from "./ui/header.js";
import { authStore } from "./core/auth-store.js";
import { bootAuth } from "./core/auth-store.js";
import {
  getRoute,
  subscribeRoute,
  requireAuth,
  isAuthOnlyRoute,
  navigate,
} from "./core/router.js";
import { startNewPlay } from "./core/current-play.js";
import { showToast } from "./ui/toast.js";
import { mountLoginView } from "./ui/auth/login-view.js";
import { mountSignupView } from "./ui/auth/signup-view.js";
import { mountResetView } from "./ui/auth/reset-view.js";
import { mountPlaysLibraryView } from "./ui/plays-library-view.js";
import { mountTeamsView } from "./ui/teams-view.js";
import { mountAccountView } from "./ui/account-view.js";
import { mountHelpView } from "./ui/help-view.js";
import { initHallenModeFromStorage } from "./ui/hallen-mode.js";
import { isEditorDirty, markEditorClean } from "./core/editor-dirty.js";

initFirebase();
startNewPlay();
initHallenModeFromStorage();
markEditorClean();

if (typeof window !== "undefined") {
  window.addEventListener("offline", () =>
    showToast("Offline — Änderungen werden lokal gespeichert"),
  );
  window.addEventListener("online", () => showToast("Wieder online — Synchronisiere …"));
  window.addEventListener("beforeunload", (event) => {
    if (!isEditorDirty()) {
      return;
    }
    event.preventDefault();
    event.returnValue = "";
  });
}

const root = document.getElementById("app");
if (!root) {
  throw new Error("Mount node #app not found");
}

// Build layered DOM.
const stageHost = document.createElement("div");
stageHost.className = "hb-stage-host";
const uiHost = document.createElement("div");
uiHost.className = "hb-ui-host";
const viewHost = document.createElement("div");
viewHost.className = "hb-view-host";
root.appendChild(viewHost);
root.appendChild(stageHost);
root.appendChild(uiHost);

mountHeader(uiHost);

let editorMounted = false;
/** @type {HTMLElement | null} */
let timelineHost = null;
/** @type {(() => void) | null} */
let unmountTimelineFn = null;
/** @type {HTMLElement | null} */
let playbackHost = null;
/** @type {(() => void) | null} */
let unmountPlaybackControlsFn = null;
/** @type {ReturnType<typeof createPlaybackEngine> | null} */
let playbackEngine = null;
/** @type {(() => void) | null} */
let unmountArrowPopoverFn = null;

function mountEditor() {
  if (editorMounted) {
    return;
  }
  mount(stageHost, { onTokenTap: ({ tokenId }) => openForToken(tokenId) });
  mountToolbar(uiHost);
  mountTokenEditor(uiHost);
  unmountArrowPopoverFn = mountArrowScenePopover(uiHost);
  if (!timelineHost) {
    timelineHost = document.createElement("div");
    timelineHost.id = "timeline";
    timelineHost.className = "timeline-root fixed bottom-0 left-0 right-0 z-40";
    uiHost.appendChild(timelineHost);
  }
  unmountTimelineFn = mountTimeline(timelineHost);

  // Playback controls below timeline
  const layers = getLayers();
  if (layers) {
    playbackHost = document.createElement("div");
    playbackHost.className = "playback-controls-root fixed bottom-0 left-0 right-0 z-50";
    uiHost.appendChild(playbackHost);
    playbackEngine = createPlaybackEngine({ tokensLayer: layers.players });
    unmountPlaybackControlsFn = mountPlaybackControls(playbackHost, {
      engine: playbackEngine,
    });
    // move timeline slightly up to avoid overlap
    if (timelineHost) {
      timelineHost.style.bottom = "64px";
    }
  }
  editorMounted = true;
}

function unmountEditor() {
  if (!editorMounted) {
    return;
  }
  unmountPlaybackControlsFn?.();
  unmountPlaybackControlsFn = null;
  if (playbackHost && playbackHost.parentNode) {
    playbackHost.parentNode.removeChild(playbackHost);
  }
  playbackHost = null;
  playbackEngine = null;
  unmountTimelineFn?.();
  unmountTimelineFn = null;
  if (timelineHost && timelineHost.parentNode) {
    timelineHost.parentNode.removeChild(timelineHost);
  }
  timelineHost = null;
  unmountArrowPopoverFn?.();
  unmountArrowPopoverFn = null;
  unmountToolbar();
  unmountTokenEditor();
  destroyStage();
  editorMounted = false;
}

function renderRoute() {
  const route = getRoute();
  const { status, user } = authStore.get();

  // Guard: require-auth routes redirect to login when signed out.
  if (requireAuth(route) && status === "signedOut") {
    navigate("login");
    return;
  }
  // Guard: auth-only routes redirect signed-in users to editor.
  if (isAuthOnlyRoute(route) && status === "signedIn" && user) {
    navigate("editor");
    return;
  }

  while (viewHost.firstChild) {
    viewHost.removeChild(viewHost.firstChild);
  }

  if (route === "editor") {
    viewHost.style.display = "none";
    stageHost.style.display = "";
    setHeaderVisible(true);
    mountEditor();
    return;
  }

  // Non-editor route → hide stage, show view host.
  unmountEditor();
  stageHost.style.display = "none";
  viewHost.style.display = "";
  setHeaderVisible(!isAuthOnlyRoute(route));

  switch (route) {
    case "login":
      mountLoginView(viewHost);
      break;
    case "signup":
      mountSignupView(viewHost);
      break;
    case "reset":
      mountResetView(viewHost);
      break;
    case "plays":
      mountPlaysLibraryView(viewHost);
      break;
    case "teams":
      mountTeamsView(viewHost);
      break;
    case "account":
      mountAccountView(viewHost);
      break;
    case "help":
      mountHelpView(viewHost);
      break;
    default: {
      // plays, teams, account — populated in later plans; render placeholder for now
      const ph = document.createElement("div");
      ph.className = "max-w-xl mx-auto mt-16 p-6 text-neutral-300";
      ph.textContent = `Route /${route} — wird in einem späteren Plan gebaut.`;
      viewHost.appendChild(ph);
    }
  }
}

subscribeRoute(renderRoute);
authStore.subscribe(renderRoute);

bootAuth()
  .catch((err) => console.warn("[auth] bootAuth failed", err))
  .finally(() => renderRoute());

// Initial render (covers case where no Firebase + bootAuth skipped).
renderRoute();
